import React from 'react';
import { usePrevious } from 'hooks/index';
import { notEqual } from 'utils';

export interface Map<K, V> {
  clear(): void;
  delete(key: K): boolean;
  entries(): IterableIterator<[K, V]>;
  forEach(
    callbackfn: (value: V, index: K, map: Map<K, V>) => void,
    thisArgument?: any,
  ): void;
  get(key: K): V;
  has(key: K): boolean;
  keys(): IterableIterator<K>;
  set(key: K, value?: V): Map<K, V>;
  size: number;
  values(): IterableIterator<V>;
  [Symbol.iterator](): IterableIterator<[K, V]>;
  [Symbol.toStringTag]: string;
}

export default function useMapFromArray<K, V>(
  array?: any[],
  key?: string,
): Map<K, V> {
  const map = React.useRef<any>(buildMap(array, key));

  const previousArray = usePrevious(array);

  const shouldRebuild =
    Array.isArray(previousArray) && notEqual(array, previousArray);

  function buildMap(array?: any[], key?: string) {
    return new Map(
      key ? (array ?? []).map((data) => [data[key], data]) : array ?? [],
    );
  }

  React.useEffect(() => {
    if (shouldRebuild) {
      map.current = buildMap(array, key);
    }
  }, [shouldRebuild]);

  return map.current as Map<K, V>;
}
